import React, { useState, useRef, useEffect } from 'react';
import { Phone, Mail, MessageCircle, ChevronRight, ChevronLeft, CheckCircle2, AlertCircle, Loader2, Navigation, Leaf, Search, X } from 'lucide-react';
import { db } from '../Libs/firebase-config.mjs';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';

const TOTAL_STEPS = 5;
const stepLabels = ['You', 'Contact', 'Goats', 'Housing', 'Payment'];

const goalOptions = [
  { value: 'try-goat-farming', label: 'Try Goat Farming', icon: '🌱', desc: 'Just starting out & exploring' },
  { value: 'dairy-income', label: 'Earn from Milk Sales', icon: '💰', desc: 'Sell milk for regular income' },
  { value: 'economic-growth', label: 'Economic Growth', icon: '📈', desc: 'Scale up and grow my wealth' },
  { value: 'food-security', label: 'Family Food Security', icon: '🏠', desc: 'Milk & meat for the family' },
  { value: 'investment', label: 'Productive Asset', icon: '💎', desc: 'Goats as a productive asset you invest in' },
];

const purposeOptions = [
  { value: 'milk', label: 'Milk Production', icon: '🥛', desc: 'Daily dairy goats' },
  { value: 'meat', label: 'Meat Production', icon: '🍖', desc: 'Goats raised for meat' },
  { value: 'both', label: 'Milk & Meat', icon: '🐐', desc: 'Dual-purpose farming' },
];

const housingOptions = [
  { value: 'home', label: 'At My Home', icon: '🏡', desc: 'Space at my current home', showGps: true },
  { value: 'rural', label: 'Rural Home / Shamba', icon: '🌾', desc: 'Different location (upcountry)', showGps: false },
  { value: 'rented', label: 'Rented Land', icon: '📋', desc: 'Planning to rent a space', showGps: true },
  { value: 'not-yet', label: 'Not Yet Decided', icon: '🤔', desc: 'Still looking for a place', showGps: false },
];

const financingOptions = [
  { value: 'cash', label: 'Cash Upfront', icon: '💵', desc: 'Pay in full — deposit required to confirm', color: '#285020' },
  { value: 'credit', label: 'Credit Financing', icon: '💳', desc: 'Flexible installment payment plan', color: '#0284c7' },
];

const kenyaCounties = [
  'Baringo','Bomet','Bungoma','Busia','Elgeyo Marakwet','Embu','Garissa','Homa Bay',
  'Isiolo','Kajiado','Kakamega','Kericho','Kiambu','Kilifi','Kirinyaga','Kisii',
  'Kisumu','Kitui','Kwale','Laikipia','Lamu','Machakos','Makueni','Mandera',
  'Marsabit','Meru','Migori','Mombasa',"Murang'a",'Nairobi','Nakuru','Nandi',
  'Narok','Nyamira','Nyandarua','Nyeri','Samburu','Siaya','Taita Taveta','Tana River',
  'Tharaka Nithi','Trans Nzoia','Turkana','Uasin Gishu','Vihiga','Wajir','West Pokot'
];

function CountyPicker({ value, onChange, placeholder = 'Search your county...' }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef(null);
  const inputRef = useRef(null);
  const filtered = kenyaCounties.filter(c => c.toLowerCase().includes(query.toLowerCase()));

  useEffect(() => {
    const handler = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => { if (open && inputRef.current) inputRef.current.focus(); }, [open]);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button type="button" onClick={() => setOpen(o => !o)} style={{
        width: '100%', padding: '1rem 1.1rem', borderRadius: '16px',
        border: `2px solid ${open ? '#285020' : '#e5e7eb'}`, background: '#fff',
        fontFamily: "'DM Sans', sans-serif", fontSize: '1rem',
        color: value ? '#111827' : '#9ca3af', textAlign: 'left', cursor: 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        transition: 'border-color 0.2s', boxSizing: 'border-box',
      }}>
        <span>{value || placeholder}</span>
        <Search size={16} color="#9ca3af" />
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 6px)', left: 0, right: 0,
          background: '#fff', borderRadius: '16px', border: '2px solid #e5e7eb',
          boxShadow: '0 12px 40px rgba(0,0,0,0.15)', zIndex: 100, overflow: 'hidden',
        }}>
          <div style={{ padding: '0.65rem', borderBottom: '1px solid #f3f4f6', background: '#fff' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', background: '#f9fafb', borderRadius: '10px', padding: '0.65rem 0.9rem' }}>
              <Search size={15} color="#9ca3af" style={{ flexShrink: 0 }} />
              <input ref={inputRef} value={query} onChange={e => setQuery(e.target.value)} placeholder="Type to filter..." style={{ border: 'none', background: 'transparent', outline: 'none', fontFamily: "'DM Sans', sans-serif", fontSize: '1rem', color: '#111827', width: '100%' }} />
              {query && <button type="button" onClick={() => setQuery('')} style={{ border: 'none', background: 'none', cursor: 'pointer', padding: 0, display: 'flex' }}><X size={14} color="#9ca3af" /></button>}
            </div>
          </div>
          <div style={{ maxHeight: '230px', overflowY: 'auto' }}>
            {filtered.length === 0
              ? <p style={{ padding: '1rem', fontFamily: "'DM Sans', sans-serif", fontSize: '0.95rem', color: '#9ca3af', textAlign: 'center' }}>No county found</p>
              : filtered.map(c => (
                <button key={c} type="button" onClick={() => { onChange(c); setOpen(false); setQuery(''); }} style={{
                  width: '100%', padding: '0.9rem 1.1rem', border: 'none',
                  background: value === c ? '#F1F5EE' : '#fff',
                  fontFamily: "'DM Sans', sans-serif", fontSize: '1rem',
                  color: value === c ? '#285020' : '#111827', textAlign: 'left',
                  cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  fontWeight: value === c ? 700 : 400, borderBottom: '1px solid #f9fafb',
                }}>
                  {c}
                  {value === c && <CheckCircle2 size={16} color="#285020" />}
                </button>
              ))
            }
          </div>
        </div>
      )}
    </div>
  );
}

function OptionCard({ selected, onClick, icon, label, desc, color, multi }) {
  return (
    <button type="button" onClick={onClick} style={{
      display: 'flex', alignItems: 'center', gap: '1rem', padding: '1rem 1.1rem',
      borderRadius: '16px', border: `2px solid ${selected ? (color || '#285020') : '#e5e7eb'}`,
      background: selected ? `${color || '#285020'}12` : '#fff', cursor: 'pointer',
      transition: 'all 0.2s ease', width: '100%', textAlign: 'left', boxSizing: 'border-box',
      boxShadow: selected ? `0 4px 16px ${color || '#285020'}20` : '0 1px 4px rgba(0,0,0,0.04)',
    }}>
      <span style={{ fontSize: '1.8rem', flexShrink: 0, lineHeight: 1 }}>{icon}</span>
      <div style={{ flex: 1 }}>
        <div style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 700, fontSize: '1rem', color: '#111827', lineHeight: 1.3 }}>{label}</div>
        {desc && <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.88rem', color: '#6b7280', marginTop: '2px', lineHeight: 1.4 }}>{desc}</div>}
      </div>
      <div style={{
        width: '24px', height: '24px', borderRadius: multi ? '7px' : '50%',
        border: `2px solid ${selected ? (color || '#285020') : '#d1d5db'}`,
        background: selected ? (color || '#285020') : '#fff',
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'all 0.2s',
      }}>
        {selected && <CheckCircle2 size={14} color="#fff" strokeWidth={3} />}
      </div>
    </button>
  );
}

function InputField({ label, name, value, onChange, type = 'text', placeholder, required, icon: Icon, hint }) {
  const [focused, setFocused] = useState(false);
  return (
    <div style={{ marginBottom: '1.1rem' }}>
      <label style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 700, fontSize: '0.95rem', color: '#374151', display: 'block', marginBottom: '0.45rem' }}>
        {label}{required && <span style={{ color: '#ef4444' }}> *</span>}
      </label>
      <div style={{ position: 'relative' }}>
        {Icon && <Icon size={18} color={focused ? '#285020' : '#9ca3af'} style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', transition: 'color 0.2s' }} />}
        <input type={type} name={name} value={value} onChange={onChange} placeholder={placeholder}
          onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
          style={{
            width: '100%', padding: Icon ? '1rem 1rem 1rem 2.9rem' : '1rem 1.1rem',
            borderRadius: '16px', border: `2px solid ${focused ? '#285020' : '#e5e7eb'}`,
            fontFamily: "'DM Sans', sans-serif", fontSize: '1rem', color: '#111827',
            outline: 'none', transition: 'border-color 0.2s', boxSizing: 'border-box', background: '#fff',
          }}
        />
      </div>
      {hint && <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.82rem', color: '#9ca3af', margin: '0.3rem 0 0 0.2rem' }}>{hint}</p>}
    </div>
  );
}

function GpsCapture({ lat, lng, onCapture, label = 'Tap to capture my location' }) {
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const getGPS = () => {
    if (!navigator.geolocation) { setErr('GPS not supported on this device.'); return; }
    setLoading(true); setErr('');
    navigator.geolocation.getCurrentPosition(
      pos => { onCapture(pos.coords.latitude.toFixed(6), pos.coords.longitude.toFixed(6)); setLoading(false); },
      () => { setErr('Could not get location. Please allow location access in your browser.'); setLoading(false); }
    );
  };
  return (
    <div style={{ marginBottom: '0.5rem' }}>
      <button type="button" onClick={getGPS} disabled={loading} style={{
        display: 'flex', alignItems: 'center', gap: '0.65rem', width: '100%',
        padding: '1rem 1.1rem', borderRadius: '14px',
        border: `2px dashed ${lat ? '#285020' : '#d1d5db'}`,
        background: lat ? '#F1F5EE' : '#f9fafb',
        fontFamily: "'DM Sans', sans-serif", fontSize: '0.95rem',
        color: lat ? '#285020' : '#6b7280', cursor: loading ? 'not-allowed' : 'pointer',
        fontWeight: 600, transition: 'all 0.2s', boxSizing: 'border-box',
      }}>
        {loading
          ? <Loader2 size={19} style={{ animation: 'spin 1s linear infinite', flexShrink: 0 }} />
          : <Navigation size={19} color={lat ? '#285020' : '#9ca3af'} style={{ flexShrink: 0 }} />
        }
        <span style={{ flex: 1 }}>{loading ? 'Getting your location...' : lat ? `📍 ${lat}, ${lng}` : label}</span>
        {lat && <CheckCircle2 size={17} color="#285020" />}
      </button>
      {err && <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.85rem', color: '#ef4444', margin: '0.35rem 0.2rem 0' }}>{err}</p>}
    </div>
  );
}

function ProgressBar({ step }) {
  return (
    <div style={{ marginBottom: '1.75rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.65rem' }}>
        {stepLabels.map((label, i) => (
          <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
            <div style={{
              width: '30px', height: '30px', borderRadius: '50%',
              background: i + 1 < step ? '#285020' : i + 1 === step ? '#285020' : '#e5e7eb',
              display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '5px',
              transition: 'all 0.3s', boxShadow: i + 1 === step ? '0 0 0 5px rgba(22,163,74,0.18)' : 'none',
            }}>
              {i + 1 < step
                ? <CheckCircle2 size={16} color="#fff" strokeWidth={3} />
                : <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.8rem', fontWeight: 800, color: i + 1 === step ? '#fff' : '#9ca3af' }}>{i + 1}</span>
              }
            </div>
            <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.7rem', fontWeight: i + 1 <= step ? 800 : 500, color: i + 1 <= step ? '#285020' : '#9ca3af', textAlign: 'center' }}>{label}</span>
          </div>
        ))}
      </div>
      <div style={{ height: '5px', background: '#e5e7eb', borderRadius: '99px', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${((step - 1) / (TOTAL_STEPS - 1)) * 100}%`, background: 'linear-gradient(90deg,#285020,#8DBB7E)', borderRadius: '99px', transition: 'width 0.5s cubic-bezier(0.4,0,0.2,1)' }} />
      </div>
    </div>
  );
}

function SectionLabel({ children }) {
  return <p style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 700, fontSize: '1rem', color: '#374151', marginBottom: '0.7rem', marginTop: '0.1rem' }}>{children}</p>;
}

export default function MbuziPlus({ isDark, onBack }) {
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const [form, setForm] = useState({
    fullName: '', currentCounty: '', currentGpsLat: '', currentGpsLng: '',
    phone: '', whatsapp: '', email: '',
    purpose: '', goals: [], quantity: '',
    housingType: '', housingCounty: '', housingSubLocation: '', housingVillage: '',
    housingGpsLat: '', housingGpsLng: '', financing: '',
  });

  const set = (name, val) => { setForm(p => ({ ...p, [name]: val })); setError(''); };
  const toggleGoal = val => { setForm(p => ({ ...p, goals: p.goals.includes(val) ? p.goals.filter(g => g !== val) : [...p.goals, val] })); setError(''); };

  const validate = () => {
    if (step === 1) {
      if (!form.fullName.trim()) return 'Please enter your full name';
      if (!form.currentCounty) return 'Please select your current county';
    }
    if (step === 2) {
      if (!form.phone.trim()) return 'Phone number is required';
      if (!form.phone.startsWith('+')) return 'Use international format e.g. +254712345678';
    }
    if (step === 3) {
      if (!form.purpose) return 'Please select a goat purpose';
      if (form.goals.length === 0) return 'Please select at least one goal';
      if (!form.quantity || parseInt(form.quantity) < 1) return 'Please enter how many goats you want';
    }
    if (step === 4) {
      if (!form.housingType) return 'Please select where you plan to keep the goats';
    }
    if (step === 5) {
      if (!form.financing) return 'Please choose a financing option';
    }
    return null;
  };

  const next = () => {
    const e = validate(); if (e) { setError(e); return; }
    setError(''); setStep(s => s + 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const back = () => { setError(''); setStep(s => s - 1); window.scrollTo({ top: 0, behavior: 'smooth' }); };

  const handleSubmit = async () => {
    const e = validate(); if (e) { setError(e); return; }
    setIsSubmitting(true); setError('');
    try {
      await addDoc(collection(db, 'mbuzi_plus_registrations'), {
        fullName: form.fullName,
        currentLocation: {
          county: form.currentCounty,
          gps: form.currentGpsLat ? { lat: parseFloat(form.currentGpsLat), lng: parseFloat(form.currentGpsLng) } : null,
        },
        contact: { phone: form.phone, whatsapp: form.whatsapp || null, email: form.email || null },
        goatInfo: { purpose: form.purpose, goals: form.goals, quantity: parseInt(form.quantity) },
        housing: {
          type: form.housingType,
          county: form.housingCounty || null, subLocation: form.housingSubLocation || null, village: form.housingVillage || null,
          gps: form.housingGpsLat ? { lat: parseFloat(form.housingGpsLat), lng: parseFloat(form.housingGpsLng) } : null,
        },
        financing: form.financing, status: 'pending', createdAt: serverTimestamp(),
      });
      setSubmitted(true);
    } catch (err) {
      console.error(err);
      setError('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const reset = () => {
    setSubmitted(false); setStep(1); setError('');
    setForm({ fullName:'',currentCounty:'',currentGpsLat:'',currentGpsLng:'',phone:'',whatsapp:'',email:'',purpose:'',goals:[],quantity:'',housingType:'',housingCounty:'',housingSubLocation:'',housingVillage:'',housingGpsLat:'',housingGpsLng:'',financing:'' });
  };

  const selectedHousing = housingOptions.find(h => h.value === form.housingType);

  if (submitted) return (
    <div style={S.page}>
      <style>{css}</style>
      <div style={{ ...S.card, textAlign: 'center', padding: '2.5rem 1.75rem' }}>
        <div style={{ fontSize: '4rem', marginBottom: '0.75rem', animation: 'bounceIn 0.6s ease' }}>🎉</div>
        <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: '2rem', color: '#111827', margin: '0 0 0.5rem' }}>You're In!</h2>
        <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '1.05rem', color: '#6b7280', marginBottom: '1.75rem', lineHeight: 1.65 }}>
          Welcome to <strong style={{ color: '#285020' }}>Mbuzi Plus</strong>, {form.fullName.split(' ')[0]}!<br />
          Our team will reach you on <strong>{form.phone}</strong> shortly.
        </p>
        <div style={{ background: '#F1F5EE', borderRadius: '16px', padding: '1.1rem', marginBottom: '1.75rem', textAlign: 'left' }}>
          <p style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 800, fontSize: '0.82rem', color: '#285020', margin: '0 0 0.65rem', letterSpacing: '0.06em' }}>YOUR SUMMARY</p>
          {[
            ['Name', form.fullName],
            ['Location', `${form.currentCounty} County`],
            ['Phone', form.phone],
            ['Purpose', purposeOptions.find(p => p.value === form.purpose)?.label],
            ['Goals', form.goals.map(g => goalOptions.find(o => o.value === g)?.label).join(', ')],
            ['Goats', `${form.quantity} goats`],
            ['Housing', selectedHousing?.label],
            ['Payment', form.financing === 'cash' ? 'Cash Upfront' : 'Credit Financing'],
          ].map(([k, v]) => v && (
            <div key={k} style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', fontFamily: "'DM Sans', sans-serif", fontSize: '0.95rem', padding: '0.45rem 0', borderBottom: '1px solid #E3EEDD' }}>
              <span style={{ color: '#6b7280', flexShrink: 0 }}>{k}</span>
              <span style={{ color: '#111827', fontWeight: 700, textAlign: 'right' }}>{v}</span>
            </div>
          ))}
        </div>
        <button onClick={reset} style={{ ...S.primaryBtn, width: '100%' }}>Register Another Farmer</button>
      </div>
    </div>
  );

  return (
    <div style={S.page}>
      <style>{css}</style>

      <div style={{ textAlign: 'center', marginBottom: '1.5rem', padding: '0 1rem' }}>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', background: '#285020', color: '#fff', borderRadius: '99px', padding: '0.45rem 1.1rem', marginBottom: '0.85rem' }}>
          <Leaf size={14} />
          <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.82rem', fontWeight: 800, letterSpacing: '0.08em' }}>MBUZI PLUS</span>
        </div>
        <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(1.9rem, 6vw, 2.5rem)', color: '#111827', margin: '0 0 0.35rem' }}>Join Mbuzi Plus</h1>
        <p style={{ fontFamily: "'DM Sans', sans-serif", color: '#6b7280', fontSize: '1.05rem', margin: 0 }}>Quality dairy goats: productive assets, right at your doorstep</p>
      </div>

      <div style={S.card}>
        <ProgressBar step={step} />

        <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
          <span style={{ fontSize: '2.4rem', display: 'block', marginBottom: '0.25rem' }}>
            {['👋','📱','🐐','🏠','💰'][step - 1]}
          </span>
          <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.95rem', color: '#9ca3af', margin: 0, fontWeight: 500 }}>
            {["Let's get to know you", "How can we reach you?", "Tell us about your goat goals", "Where will the goats live?", "Almost there — how will you pay?"][step - 1]}
          </p>
        </div>

        {/* STEP 1 */}
        {step === 1 && (
          <div style={S.stepWrap}>
            <InputField label="Full Name" name="fullName" value={form.fullName} onChange={e => set(e.target.name, e.target.value)} placeholder="e.g. Jane Wanjiku" required />
            <div style={{ marginBottom: '1.1rem' }}>
              <label style={S.fieldLabel}>Your Current County <span style={{ color: '#ef4444' }}>*</span></label>
              <CountyPicker value={form.currentCounty} onChange={v => set('currentCounty', v)} />
            </div>
            <div>
              <label style={S.fieldLabel}>Your GPS Location <span style={{ color: '#9ca3af', fontWeight: 400, fontSize: '0.85rem' }}>— optional</span></label>
              <GpsCapture lat={form.currentGpsLat} lng={form.currentGpsLng} onCapture={(lat, lng) => { set('currentGpsLat', lat); set('currentGpsLng', lng); }} label="Tap to capture my current location" />
              <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.83rem', color: '#9ca3af', margin: '0.3rem 0.2rem 0' }}>Helps us find the nearest Mbuzi Plus agent to you.</p>
            </div>
          </div>
        )}

        {/* STEP 2 */}
        {step === 2 && (
          <div style={S.stepWrap}>
            <InputField label="Phone Number" name="phone" value={form.phone} onChange={e => set(e.target.name, e.target.value)} type="tel" placeholder="+254 7XX XXX XXX" required icon={Phone} hint="Include country code e.g. +254" />
            <InputField label="WhatsApp Number" name="whatsapp" value={form.whatsapp} onChange={e => set(e.target.name, e.target.value)} type="tel" placeholder="+254 7XX XXX XXX (if different)" icon={MessageCircle} />
            <InputField label="Email Address" name="email" value={form.email} onChange={e => set(e.target.name, e.target.value)} type="email" placeholder="jane@example.com (optional)" icon={Mail} />
          </div>
        )}

        {/* STEP 3 */}
        {step === 3 && (
          <div style={S.stepWrap}>
            <SectionLabel>What's the purpose? <span style={{ color: '#ef4444' }}>*</span></SectionLabel>
            <div style={S.optionStack}>
              {purposeOptions.map(o => <OptionCard key={o.value} selected={form.purpose === o.value} onClick={() => set('purpose', o.value)} icon={o.icon} label={o.label} desc={o.desc} />)}
            </div>

            <SectionLabel>
              Your goals <span style={{ color: '#ef4444' }}>*</span>
              <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.85rem', color: '#9ca3af', fontWeight: 500 }}> — pick all that apply</span>
            </SectionLabel>
            <div style={S.optionStack}>
              {goalOptions.map(o => <OptionCard key={o.value} selected={form.goals.includes(o.value)} onClick={() => toggleGoal(o.value)} icon={o.icon} label={o.label} desc={o.desc} multi />)}
            </div>

            {form.goals.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.45rem', margin: '-0.4rem 0 1rem' }}>
                {form.goals.map(g => {
                  const opt = goalOptions.find(o => o.value === g);
                  return <span key={g} style={{ background: '#E3EEDD', color: '#285020', fontFamily: "'DM Sans', sans-serif", fontSize: '0.85rem', fontWeight: 700, borderRadius: '99px', padding: '0.3rem 0.85rem' }}>{opt?.icon} {opt?.label}</span>;
                })}
              </div>
            )}

            <InputField label="How many goats do you want?" name="quantity" value={form.quantity} onChange={e => set(e.target.name, e.target.value)} type="number" placeholder="e.g. 5" required />
          </div>
        )}

        {/* STEP 4 */}
        {step === 4 && (
          <div style={S.stepWrap}>
            <SectionLabel>Where will you keep the goats? <span style={{ color: '#ef4444' }}>*</span></SectionLabel>
            <div style={S.optionStack}>
              {housingOptions.map(o => <OptionCard key={o.value} selected={form.housingType === o.value} onClick={() => set('housingType', o.value)} icon={o.icon} label={o.label} desc={o.desc} />)}
            </div>

            {selectedHousing?.showGps && (
              <div style={{ background: '#F1F5EE', borderRadius: '16px', padding: '1rem', marginTop: '0.25rem', animation: 'fadeUp 0.3s ease' }}>
                <p style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 800, fontSize: '0.92rem', color: '#285020', margin: '0 0 0.75rem' }}>📍 Pin the goat location</p>
                <GpsCapture lat={form.housingGpsLat} lng={form.housingGpsLng} onCapture={(lat, lng) => { set('housingGpsLat', lat); set('housingGpsLng', lng); }} label="Use GPS for this location" />
              </div>
            )}

            {form.housingType === 'rural' && (
              <div style={{ background: '#F1F5EE', borderRadius: '16px', padding: '1rem', marginTop: '0.5rem', animation: 'fadeUp 0.3s ease' }}>
                <p style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 800, fontSize: '0.92rem', color: '#285020', margin: '0 0 0.85rem' }}>🌾 Rural Home Details</p>
                <div style={{ marginBottom: '1rem' }}>
                  <label style={S.fieldLabel}>County</label>
                  <CountyPicker value={form.housingCounty} onChange={v => set('housingCounty', v)} placeholder="Select rural county" />
                </div>
                <InputField label="Sub-location / Area" name="housingSubLocation" value={form.housingSubLocation} onChange={e => set(e.target.name, e.target.value)} placeholder="e.g. Mathioya" />
                <InputField label="Village Name" name="housingVillage" value={form.housingVillage} onChange={e => set(e.target.name, e.target.value)} placeholder="e.g. Kamwangi" />
              </div>
            )}
          </div>
        )}

        {/* STEP 5 */}
        {step === 5 && (
          <div style={S.stepWrap}>
            <SectionLabel>How would you like to pay? <span style={{ color: '#ef4444' }}>*</span></SectionLabel>
            <div style={S.optionStack}>
              {financingOptions.map(o => <OptionCard key={o.value} selected={form.financing === o.value} onClick={() => set('financing', o.value)} icon={o.icon} label={o.label} desc={o.desc} color={o.color} />)}
            </div>

            {form.financing === 'cash' && (
              <div style={{ background: '#fefce8', border: '2px solid #fde047', borderRadius: '14px', padding: '1rem 1.1rem', marginBottom: '1rem', animation: 'fadeUp 0.3s ease' }}>
                <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.95rem', color: '#854d0e', margin: 0, lineHeight: 1.65 }}>
                  💡 A <strong>deposit</strong> will be required to confirm your order. Our team contacts you within 24hrs.
                </p>
              </div>
            )}
            {form.financing === 'credit' && (
              <div style={{ background: '#eff6ff', border: '2px solid #bfdbfe', borderRadius: '14px', padding: '1rem 1.1rem', marginBottom: '1rem', animation: 'fadeUp 0.3s ease' }}>
                <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.95rem', color: '#1e40af', margin: 0, lineHeight: 1.65 }}>
                  💡 Our team will guide you through a simple <strong>credit assessment</strong> and a flexible repayment plan.
                </p>
              </div>
            )}

            <div style={{ background: '#f9fafb', borderRadius: '16px', padding: '1.1rem', border: '2px solid #e5e7eb' }}>
              <p style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 800, fontSize: '0.82rem', color: '#285020', margin: '0 0 0.7rem', letterSpacing: '0.05em' }}>✅ REVIEW YOUR INFO</p>
              {[
                ['Name', form.fullName],
                ['County', `${form.currentCounty} County`],
                ['Phone', form.phone],
                ['Purpose', purposeOptions.find(p => p.value === form.purpose)?.label],
                ['Goals', `${form.goals.length} selected`],
                ['Goats', `${form.quantity} goats`],
                ['Housing', selectedHousing?.label],
              ].map(([k, v]) => v && (
                <div key={k} style={{ display: 'flex', justifyContent: 'space-between', fontFamily: "'DM Sans', sans-serif", fontSize: '0.95rem', padding: '0.4rem 0', borderBottom: '1px solid #f3f4f6' }}>
                  <span style={{ color: '#9ca3af' }}>{k}</span>
                  <span style={{ color: '#111827', fontWeight: 700 }}>{v}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {error && (
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.65rem', background: '#fef2f2', border: '2px solid #fecaca', borderRadius: '12px', padding: '0.9rem 1rem', marginTop: '1rem' }}>
            <AlertCircle size={18} color="#ef4444" style={{ flexShrink: 0, marginTop: '1px' }} />
            <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.95rem', color: '#dc2626', margin: 0, lineHeight: 1.5 }}>{error}</p>
          </div>
        )}

        <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.5rem' }}>
          {step > 1 && (
            <button type="button" onClick={back} style={S.ghostBtn}>
              <ChevronLeft size={19} /> Back
            </button>
          )}
          {step < TOTAL_STEPS ? (
            <button type="button" onClick={next} style={S.primaryBtn}>
              Continue <ChevronRight size={19} />
            </button>
          ) : (
            <button type="button" onClick={handleSubmit} disabled={isSubmitting} style={{ ...S.primaryBtn, opacity: isSubmitting ? 0.7 : 1, cursor: isSubmitting ? 'not-allowed' : 'pointer' }}>
              {isSubmitting ? <><Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} /> Submitting...</> : <>🐐 Join Mbuzi Plus</>}
            </button>
          )}
        </div>
      </div>

      <p style={{ textAlign: 'center', fontFamily: "'DM Sans', sans-serif", fontSize: '0.88rem', color: '#d1d5db', marginTop: '1.25rem' }}>Step {step} of {TOTAL_STEPS}</p>
    </div>
  );
}

const S = {
  page: {
    minHeight: '100vh',
    background: 'linear-gradient(160deg, #F1F5EE 0%, #f8fafc 55%, #E3EEDD 100%)',
    paddingTop: '90px', paddingBottom: '3rem',
  },
  card: {
    maxWidth: '520px', margin: '0 auto',
    background: '#fff', borderRadius: '24px',
    padding: 'clamp(1.25rem, 5vw, 2rem)',
    boxShadow: '0 8px 40px rgba(0,0,0,0.09)',
    animation: 'fadeUp 0.4s ease',
    marginLeft: 'clamp(0.75rem, 5vw, auto)',
    marginRight: 'clamp(0.75rem, 5vw, auto)',
  },
  stepWrap: { animation: 'fadeUp 0.35s ease' },
  optionStack: { display: 'flex', flexDirection: 'column', gap: '0.65rem', marginBottom: '1.35rem' },
  fieldLabel: { fontFamily: "'DM Sans', sans-serif", fontWeight: 700, fontSize: '0.95rem', color: '#374151', display: 'block', marginBottom: '0.45rem' },
  primaryBtn: {
    flex: 2, padding: '1.1rem 1.25rem', borderRadius: '16px', border: 'none',
    background: 'linear-gradient(135deg,#285020,#3E7A32)', color: '#fff',
    fontFamily: "'DM Sans', sans-serif", fontWeight: 800, fontSize: '1.05rem',
    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
    gap: '0.45rem', boxShadow: '0 4px 16px rgba(22,163,74,0.35)', transition: 'transform 0.15s',
  },
  ghostBtn: {
    flex: 1, padding: '1.1rem', borderRadius: '16px', border: '2px solid #e5e7eb',
    background: '#fff', fontFamily: "'DM Sans', sans-serif", fontWeight: 700,
    fontSize: '1.05rem', color: '#374151', cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.35rem',
  },
};

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700&family=DM+Sans:ital,opsz,wght@0,9..40,400;0,9..40,500;0,9..40,700;0,9..40,800&display=swap');
  @keyframes fadeUp { from { opacity:0; transform:translateY(18px); } to { opacity:1; transform:translateY(0); } }
  @keyframes spin { from { transform:rotate(0deg); } to { transform:rotate(360deg); } }
  @keyframes bounceIn { 0%{transform:scale(0.5);opacity:0} 70%{transform:scale(1.1)} 100%{transform:scale(1);opacity:1} }
  * { box-sizing: border-box; -webkit-tap-highlight-color: transparent; }
  input, select, button { -webkit-appearance: none; appearance: none; }
  button:not(:disabled):active { transform: scale(0.97); }
`;